/* Happy Horn Studio. No analytics, external libraries or synthetic sound samples. */
(() => {
  'use strict';
  const scope = window;
  const root = () => (scope.Shopify && scope.Shopify.routes && scope.Shopify.routes.root) || '/';
  const cartURL = () => (scope.HappyHorn && scope.HappyHorn.cartURL) || `${root()}cart`;
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  let cartBusy = false;
  const demos = new WeakMap();
  const pauseOtherMedia = current => {
    document.querySelectorAll('audio, video').forEach(other => {
      if (other !== current && !other.paused) other.pause();
    });
  };
  function initDemo(video) {
    if (demos.has(video)) return;
    const section = video.closest('[data-hh-demo-section]');
    if (!section) return;
    const button = section.querySelector('[data-hh-demo-play]');
    const label = button?.querySelector('[data-hh-demo-label]');
    const message = section.querySelector('[data-hh-sound-message]');
    let pending = false;
    let attempt = 0;
    let needsReload = false;
    const sync = () => {
      const playing = !video.paused && !video.ended;
      if (label) label.textContent = playing ? 'Pause the demo' : pending ? 'Loading demo…' : 'Play the real demo';
      if (button) {
        button.setAttribute('aria-pressed', String(playing));
        button.setAttribute('aria-busy', String(pending));
      }
      section.classList.toggle('is-playing', playing);
    };
    const clearMessage = () => {
      if (message) { message.textContent = ''; message.removeAttribute('data-error'); }
    };
    const fail = error => {
      pending = false;
      if (message) {
        message.dataset.error = 'true';
        message.textContent = error?.name === 'NotAllowedError'
          ? 'Your browser blocked playback. Press play on the video to hear the demo.'
          : 'The demo could not load. Check your connection, then press play to try again.';
      }
      sync();
    };
    const play = () => {
      if (pending || (!video.paused && !video.ended)) return;
      const currentAttempt = ++attempt;
      pending = true;
      clearMessage();
      sync();
      try {
        if (needsReload || video.error) { needsReload = false; video.load(); }
        if (video.ended) video.currentTime = 0;
        // Called synchronously from a click: never unmute or start the demo on load.
        video.muted = false;
        pauseOtherMedia(video);
        const playback = video.play();
        if (playback?.catch) playback.catch(error => {
          if (currentAttempt !== attempt) return;
          needsReload = error.name !== 'NotAllowedError' && error.name !== 'AbortError';
          fail(error);
        });
      } catch (error) {
        if (currentAttempt === attempt) { needsReload = true; fail(error); }
      }
    };
    const pause = () => { ++attempt; pending = false; video.pause(); sync(); };
    demos.set(video, { play, pause, button, isPending: () => pending });
    video.addEventListener('play', () => { pending = false; clearMessage(); sync(); });
    video.addEventListener('pause', () => { ++attempt; pending = false; sync(); });
    video.addEventListener('ended', () => { pending = false; sync(); });
    video.addEventListener('error', () => {
      ++attempt; needsReload = true; pending = false; video.pause(); fail(video.error);
    });
    sync();
  }
  function init(container = document) {
    container.querySelectorAll('video[data-hh-demo]').forEach(initDemo);
    container.querySelectorAll('[data-hh-pack-group]').forEach(group => {
      if (group.dataset.initialized) return;
      group.dataset.initialized = 'true';
      const choices = Array.from(group.querySelectorAll('[data-hh-pack]'));
      const panels = Array.from(group.querySelectorAll('[data-hh-panel]'));
      const select = key => {
        choices.forEach(button => button.setAttribute('aria-pressed', String(button.dataset.hhPack === key)));
        panels.forEach(panel => { panel.hidden = panel.dataset.hhPanel !== key; });
      };
      choices.forEach(button => button.addEventListener('click', () => select(button.dataset.hhPack)));
      const active = choices.find(button => button.getAttribute('aria-pressed') === 'true') || choices[0];
      if (active) select(active.dataset.hhPack);
    });
    container.querySelectorAll('[data-hh-product-form]').forEach(form => {
      if (form.dataset.initialized) return;
      form.dataset.initialized = 'true';
      const variantSelector = form.querySelector('[data-hh-variant]');
      if (variantSelector) variantSelector.addEventListener('change', () => {
        const choice = variantSelector.selectedOptions[0];
        if (!choice) return;
        const buy = form.querySelector('[type=submit]');
        const isPair = Number(form.dataset.hhQuantity) === 2;
        const available = choice.dataset.available === 'true';
        const validPrice = !isPair || (Number(choice.dataset.priceCents) === 7999 && choice.dataset.currency === 'NZD');
        if (buy) {
          const live = buy.getAttribute('data-hh-live') === 'true';
          buy.disabled = !live || !available || !validPrice;
          buy.textContent = !live ? 'Online ordering is being prepared' : !available
            ? (isPair ? 'Two horns are not currently available' : 'Currently unavailable')
            : !validPrice ? 'Pair offer unavailable' : buy.dataset.hhLabel || 'Add to cart';
        }
        const price = form.closest('[data-hh-panel]')?.querySelector('[data-hh-variant-price]');
        if (price && !isPair) price.textContent = choice.dataset.price;
        const message = form.querySelector('[data-hh-message]');
        if (message) message.textContent = '';
      });
      form.addEventListener('submit', async event => {
        // Native Shopify form submission remains available without JavaScript.
        if (!scope.fetch) return;
        event.preventDefault();
        if (form.dataset.pending === 'true') return;
        const button = form.querySelector('[type=submit]');
        if (!button || button.disabled) return;
        const message = form.querySelector('[data-hh-message]');
        const original = button.textContent;
        const payload = new FormData(form);
        form.dataset.pending = 'true'; button.disabled = true; button.textContent = 'Adding…';
        if (message) { message.textContent = ''; message.removeAttribute('data-error'); }
        let added = false;
        try {
          const response = await fetch(`${root()}cart/add.js`, { method: 'POST', body: payload, credentials: 'same-origin', headers: { 'Accept': 'application/json' } });
          const data = await response.json().catch(() => ({}));
          if (!response.ok) {
            const detail = typeof data.description === 'string' ? data.description : '';
            if (response.status === 404 || /cannot find variant|not available/i.test(detail)) {
              throw new Error('This product is not currently available to add. Please try again later or contact Happy Horn.');
            }
            throw new Error(`${detail || 'This item could not be added.'} Check your cart before trying again.`);
          }
          added = true;
          if (message) message.textContent = 'Added. Opening your cart…';
          // Optional animation starts only after Shopify confirms the add.
          try {
            document.dispatchEvent(new scope.CustomEvent('hh:cart-added', { detail: { form, quantity: Number(form.dataset.hhQuantity) } }));
            if (!reduced.matches && !document.hidden && button.dataset.hhRace === 'success') {
              await new Promise(resolve => {
                const finish = () => {
                  scope.clearTimeout(timer);
                  reduced.removeEventListener?.('change', onPreference);
                  document.removeEventListener('visibilitychange', onVisibility);
                  resolve();
                };
                const onPreference = () => { if (reduced.matches) finish(); };
                const onVisibility = () => { if (document.hidden) finish(); };
                const timer = scope.setTimeout(finish, 780);
                reduced.addEventListener?.('change', onPreference);
                document.addEventListener('visibilitychange', onVisibility);
              });
            }
          } catch (_) {}
          scope.location.assign(cartURL());
        } catch (error) {
          if (message) {
            message.dataset.error = 'true';
            message.textContent = error instanceof TypeError
              ? 'We could not confirm the result. Check your cart before trying again; your item may already have been added.'
              : error.message || 'Unable to add this item. Please check your cart before trying again.';
          }
        } finally {
          if (!added) { form.dataset.pending = 'false'; button.disabled = false; button.textContent = original; }
        }
      });
    });
    container.querySelectorAll('[data-hh-cart-change]').forEach(form => {
      if (form.dataset.initialized) return;
      form.dataset.initialized = 'true';
      form.addEventListener('submit', async event => {
        if (!scope.fetch) return;
        event.preventDefault();
        if (cartBusy) return;
        const quantity = Number(form.elements.quantity.value);
        const message = form.querySelector('[data-hh-message]');
        if (!Number.isInteger(quantity) || quantity < 0) {
          if (message) { message.dataset.error = 'true'; message.textContent = 'Enter a whole number of horns, or 0 to remove this line.'; }
          return;
        }
        // Discounted units can be split into separate lines. Always use the live line key.
        const payload = { id: form.elements.id.value, quantity };
        const controls = Array.from(document.querySelectorAll('[data-hh-cart-change] button, [name="checkout"]'));
        const oldStates = controls.map(control => control.disabled);
        cartBusy = true;
        controls.forEach(control => { control.disabled = true; });
        if (message) { message.removeAttribute('data-error'); message.textContent = 'Updating…'; }
        let updated = false;
        try {
          const response = await fetch(`${root()}cart/change.js`, {
            method: 'POST', credentials: 'same-origin',
            headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
          });
          const data = await response.json().catch(() => ({}));
          if (!response.ok) throw new Error(typeof data.description === 'string' ? data.description : 'This quantity is not available. Please refresh your cart.');
          updated = true;
          // Re-render from Shopify so regenerated line keys and all discounts remain authoritative.
          scope.location.assign(cartURL());
        } catch (error) {
          if (message) {
            message.dataset.error = 'true';
            message.textContent = error instanceof TypeError
              ? 'We could not confirm the update. Refresh your cart before trying again.'
              : error.message || 'Unable to update your cart. Please refresh and try again.';
          }
        } finally {
          if (!updated) {
            cartBusy = false;
            controls.forEach((control, index) => { control.disabled = oldStates[index]; });
          }
        }
      });
    });
    container.querySelectorAll('video[data-hh-ambient]').forEach(video => {
      if (video.dataset.initialized) return;
      video.dataset.initialized = 'true';
      if (reduced.matches) video.pause();
      const button = video.parentElement.querySelector('[data-hh-video-toggle]');
      const sync = () => { if(button) { button.textContent = video.paused ? 'Play video' : 'Pause video'; button.setAttribute('aria-pressed', String(!video.paused)); } };
      button?.addEventListener('click', () => { if (video.paused) video.play().catch(sync); else video.pause(); });
      video.addEventListener('play',sync); video.addEventListener('pause',sync); sync();
    });
    container.querySelectorAll('[data-hh-menu-button]').forEach(button => {
      if(button.dataset.initialized) return;
      button.dataset.initialized = 'true';
      const menu = document.getElementById(button.getAttribute('aria-controls'));
      if(!menu) return;
      const close = () => { menu.hidden = true; button.setAttribute('aria-expanded','false'); button.setAttribute('aria-label','Open menu'); };
      button.addEventListener('click',() => { const opened = button.getAttribute('aria-expanded')==='true'; menu.hidden=opened; button.setAttribute('aria-expanded', String(!opened)); button.setAttribute('aria-label', opened?'Open menu':'Close menu'); });
      menu.querySelectorAll('a').forEach(link => link.addEventListener('click',close));
      menu.addEventListener('keydown',event => { if(event.key==='Escape') { close(); button.focus(); } });
      button.addEventListener('keydown',event => { if(event.key==='Escape') close(); });
      matchMedia('(min-width: 768px)').addEventListener('change',event => { if(event.matches) close(); });
    });
  }
  const start = () => {
    init();
    const sticky=document.querySelector('[data-hh-sticky]');
    const offer=document.getElementById('shop');
    if(sticky && offer) {
      const update=()=>{const rect=offer.getBoundingClientRect();const offerVisible=rect.top<innerHeight && rect.bottom>0;sticky.classList.toggle('is-visible',scrollY>520&&!offerVisible);};
      addEventListener('scroll',update,{passive:true});addEventListener('resize',update);update();
    }
  };
  document.addEventListener('play', event => {
    if (event.target.matches('audio, video')) pauseOtherMedia(event.target);
  }, true);
  document.addEventListener('click', event => {
    const trigger = event.target.closest('[data-hh-hear], [data-hh-demo-play]');
    if (!trigger || event.defaultPrevented || event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const hear = trigger.hasAttribute('data-hh-hear');
    const video = hear ? document.querySelector('#sounds video[data-hh-demo]') : document.getElementById(trigger.getAttribute('aria-controls'));
    if (!video) return; // Preserve the anchor fallback when the demo is absent.
    const demo = demos.get(video);
    if (!demo) return;
    event.preventDefault();
    if (hear) {
      video.scrollIntoView({ behavior: reduced.matches ? 'auto' : 'smooth', block: 'center' });
      demo.button?.focus({ preventScroll: true });
      demo.play();
    } else if (demo.isPending() || (!video.paused && !video.ended)) demo.pause();
    else demo.play();
  });
  if(document.readyState==='loading') document.addEventListener('DOMContentLoaded', start); else start();
  document.addEventListener('shopify:section:load',event=>init(event.target));
  document.addEventListener('shopify:section:unload', event => {
    event.target.querySelectorAll('video[data-hh-demo]').forEach(video => demos.get(video)?.pause());
  });
  // Keep the real photo cropped neatly and approved artwork fully visible.
  function selectGalleryImage(button) {
    const gallery = button.closest('[data-hh-gallery]');
    const image = gallery?.querySelector('[data-hh-gallery-image]');
    if (!gallery || !image || !button.dataset.hhSrc) return;
    const label = button.dataset.hhLabel || 'Happy Horn image';
    gallery.querySelectorAll('[data-hh-gallery-choice]').forEach(choice => {
      choice.setAttribute('aria-pressed', String(choice === button));
    });
    image.alt = button.dataset.hhAlt || label;
    image.src = button.dataset.hhSrc;
    gallery.dataset.hhFit = button.dataset.hhFit === 'cover' ? 'cover' : 'contain';
    gallery.querySelectorAll('[data-hh-gallery-open]').forEach(link => {
      link.href = button.dataset.hhSrc;
      link.setAttribute('aria-label', `Open ${label} full size`);
    });
    const status = gallery.querySelector('[data-hh-gallery-status]');
    if (status) status.textContent = label;
  }
  document.addEventListener('click', event => {
    const button = event.target.closest('[data-hh-gallery-choice]');
    if (!button || event.defaultPrevented || event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    selectGalleryImage(button);
  });
  document.addEventListener('keydown', event => {
    const button = event.target.closest('[data-hh-gallery-choice]');
    if (!button || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    const gallery = button.closest('[data-hh-gallery]');
    const choices = Array.from(gallery.querySelectorAll('[data-hh-gallery-choice]'));
    const current = choices.indexOf(button);
    let next = event.key === 'Home' ? 0 : event.key === 'End' ? choices.length - 1 : current + (event.key === 'ArrowRight' ? 1 : -1);
    next = (next + choices.length) % choices.length;
    event.preventDefault();
    selectGalleryImage(choices[next]);
    choices[next].focus({ preventScroll: true });
    choices[next].scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'auto' });
  });
  document.addEventListener('error', event => {
    if (!event.target.matches?.('[data-hh-gallery-image]')) return;
    const status = event.target.closest('[data-hh-gallery]')?.querySelector('[data-hh-gallery-status]');
    if (status) status.textContent = 'This image could not load. Please choose another image or try the full-size link.';
  }, true);
})();

/* Happy Horn motion polish. Optional enhancement: normal content is always visible. */
(() => {
  'use strict';
  if (!window.matchMedia || !window.Element || !Element.prototype.animate) return;
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const active = new Map();
  const seen = new WeakSet();
  const gallerySources = new WeakMap();
  const packChoices = new WeakMap();
  const entrances = '.hh-brand-gallery, .hh-offer-copy, .hh-sound-shell, .hh-setup-intro, .hh-setup-steps .hh-step, #features .hh-section-head, .hh-benefit, .hh-faq-intro, .hh-last-call';
  let observer;
  function stop(element) {
    const animation = active.get(element);
    if (animation) { animation.cancel(); active.delete(element); }
  }
  function animate(element, frames, options) {
    if (!element || reduced.matches || document.hidden || !element.isConnected) return;
    stop(element);
    // No persistent styles or fill mode: interruption leaves the normal visible element.
    const animation = element.animate(frames, options);
    active.set(element, animation);
    const finished = () => { if (active.get(element) === animation) active.delete(element); };
    animation.addEventListener('finish', finished, { once: true });
    animation.addEventListener('cancel', finished, { once: true });
  }
  function enter(element, delay = 0) {
    animate(element, [{ opacity: .6, transform: 'translateY(16px)' }, { opacity: 1, transform: 'translateY(0)' }], {
      duration: 480, delay, easing: 'cubic-bezier(.2,.75,.3,1)'
    });
  }
  function galleryReady(image) {
    if (!image?.matches?.('[data-hh-gallery-image]') || !image.complete || !image.naturalWidth) return;
    const source = image.currentSrc || image.src;
    if (gallerySources.get(image) === source) return;
    const previous = gallerySources.get(image);
    gallerySources.set(image, source);
    if (previous) animate(image, [{ opacity: .55 }, { opacity: 1 }], { duration: 240, easing: 'ease-out' });
  }
  function init(container = document) {
    container.querySelectorAll('[data-hh-gallery-image]').forEach(image => {
      if (!gallerySources.has(image)) gallerySources.set(image, image.currentSrc || image.src);
    });
    container.querySelectorAll('[data-hh-pack-group]').forEach(group => {
      if (!packChoices.has(group)) packChoices.set(group, group.querySelector('[data-hh-pack][aria-pressed="true"]')?.dataset.hhPack);
    });
    if (reduced.matches || !('IntersectionObserver' in window)) return;
    if (!observer) observer = new IntersectionObserver(entries => {
      let delay = 0;
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        observer.unobserve(entry.target);
        if (seen.has(entry.target)) return;
        seen.add(entry.target);
        enter(entry.target, Math.min(delay, 120));
        delay += 60;
      });
    }, { threshold: .08 });
    container.querySelectorAll(entrances).forEach(element => { if (!seen.has(element)) observer.observe(element); });
  }
  document.addEventListener('load', event => galleryReady(event.target), true);
  document.addEventListener('click', event => {
    if (event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const pack = event.target.closest('[data-hh-pack]');
    if (pack) {
      const group = pack.closest('[data-hh-pack-group]');
      if (group && packChoices.get(group) !== pack.dataset.hhPack) {
        packChoices.set(group, pack.dataset.hhPack);
        animate(group.querySelector('[data-hh-panel]:not([hidden])'), [{ opacity: .65, transform: 'translateY(5px)' }, { opacity: 1, transform: 'translateY(0)' }], { duration: 200, easing: 'ease-out' });
      }
    }
    const menuButton = event.target.closest('[data-hh-menu-button]');
    if (menuButton?.getAttribute('aria-expanded') === 'true') {
      animate(document.getElementById(menuButton.getAttribute('aria-controls')), [{ opacity: .65, transform: 'translateY(-5px)' }, { opacity: 1, transform: 'translateY(0)' }], { duration: 180, easing: 'ease-out' });
    }
    const thumb = event.target.closest('[data-hh-gallery-choice]');
    if (thumb) requestAnimationFrame(() => galleryReady(thumb.closest('[data-hh-gallery]')?.querySelector('[data-hh-gallery-image]')));
  });
  document.addEventListener('keydown', event => {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    const thumb = event.target.closest('[data-hh-gallery-choice]');
    if (thumb) requestAnimationFrame(() => galleryReady(thumb.closest('[data-hh-gallery]')?.querySelector('[data-hh-gallery-image]')));
  });
  document.addEventListener('toggle', event => {
    if (!event.target.matches?.('.hh-faq-item, .hh-setup-guide, .hh-included-details') || !event.target.open) return;
    animate(event.target.querySelector(':scope > div'), [{ opacity: .65, transform: 'translateY(-4px)' }, { opacity: 1, transform: 'translateY(0)' }], { duration: 200, easing: 'ease-out' });
  }, true);
  const stopAll = () => { active.forEach(animation => animation.cancel()); active.clear(); };
  const preferenceChanged = () => {
    stopAll();
    if (reduced.matches) observer?.disconnect();
    else init();
  };
  if (reduced.addEventListener) reduced.addEventListener('change', preferenceChanged);
  else reduced.addListener(preferenceChanged);
  document.addEventListener('visibilitychange', () => { if (document.hidden) stopAll(); });
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => {
    event.target.querySelectorAll(entrances).forEach(element => observer?.unobserve(element));
    active.forEach((animation, element) => { if (event.target.contains(element)) stop(element); });
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
})();

/* Toad feedback is cosmetic; only the confirmed-add event can start a race. */
(() => {
  const key = 'hh-toad-cart-race-v1';
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const bound = new WeakSet();
  const read = () => {
    try {
      const value = window.sessionStorage.getItem(key);
      return value === 'running' || value === 'paused' ? value : '';
    } catch (_) { return ''; }
  };
  const write = value => {
    try {
      if (value) window.sessionStorage.setItem(key, value);
      else window.sessionStorage.removeItem(key);
    } catch (_) {}
  };
  const sync = () => {
    document.querySelectorAll('[data-hh-race-cart]').forEach(cart => {
      if (Number(cart.dataset.hhHornCount) < 1) write('');
      const track = cart.querySelector('[data-hh-cart-race]');
      if (!track) return;
      const state = read();
      track.hidden = !state || Number(cart.dataset.hhHornCount) < 1;
      track.dataset.motion = reduced.matches ? 'reduced' : document.hidden ? 'hidden' : state || 'paused';
      const toggle = track.querySelector('[data-hh-race-toggle]');
      if (!toggle) return;
      toggle.hidden = reduced.matches;
      toggle.setAttribute('aria-pressed', String(state === 'paused'));
      toggle.textContent = state === 'paused' ? 'Resume animation' : 'Pause animation';
      if (!bound.has(toggle)) {
        bound.add(toggle);
        toggle.addEventListener('click', () => {
          if (!read()) return;
          write(read() === 'paused' ? 'running' : 'paused');
          sync();
        });
      }
    });
  };
  const stopForCheckout = button => {
    if (!button || button.disabled || !button.closest('[data-hh-race-cart]')) return;
    write('');
    sync();
  };
  document.addEventListener('hh:cart-added', event => {
    const quantity = Number(event.detail?.quantity);
    if (!Number.isInteger(quantity) || quantity < 1) return;
    const button = event.detail?.form?.querySelector('[type=submit]');
    if (!button) return;
    write('running');
    button.dataset.hhRace = 'success';
    button.textContent = 'Added — opening your cart';
  });
  // Never intercept submission or delay Shopify checkout.
  document.addEventListener('click', event => stopForCheckout(event.target.closest?.('[name="checkout"]')));
  document.addEventListener('submit', event => {
    const button = event.submitter?.name === 'checkout' ? event.submitter : event.target.querySelector?.('[name="checkout"]');
    stopForCheckout(button);
  });
  document.addEventListener('visibilitychange', sync);
  document.addEventListener('shopify:section:load', sync);
  if (reduced.addEventListener) reduced.addEventListener('change', sync);
  else reduced.addListener?.(sync);
  window.addEventListener?.('pageshow', sync);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', sync);
  else sync();
})();
